import type { ReactNode } from "react";

import type {
  LookbookItemDataFragment,
  LookbookSectionDataFragment,
} from "../../graphql/types";
import { Media } from "../Media";
import { OverlayButton } from "../OverlayButton";
import { PoweredByEndear } from "../PoweredByEndear";

export interface StorySectionProps {
  section: LookbookSectionDataFragment;
  position: number;
  firstItem: LookbookItemDataFragment | undefined;
  lookbookTitle: string;
  brandName: string;
  showBranding: boolean;
  avatar: ReactNode;
  isCurrent: boolean;
  isNext: boolean;
  isPrevious: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
}

export function StorySection(props: StorySectionProps) {
  const {
    section,
    position,
    firstItem,
    lookbookTitle,
    showBranding,
    brandName,
    avatar,
    isCurrent,
    isNext,
    isPrevious,
    onNext,
    onPrevious,
  } = props;

  const sectionTitle = section.title.length
    ? section.title
    : position === 0
      ? brandName
      : undefined;

  return (
    <section
      className="lookbook--story--section lookbook--story--item lookbook--story--item--cover"
      data-id={section.id}
      data-type="section"
      data-position={position}
      data-current={isCurrent}
      data-next={isNext}
      data-previous={isPrevious}>
      {firstItem ? (
        <div className="lookbook--story--section--underlay">
          <Media
            visible={isCurrent}
            prefer="cover"
            size={null}
            media={firstItem.media}
            prefetch={isCurrent || isNext || isPrevious}
            sizes={[
              ["(min-width: 1280px)", "25vw"],
              ["(min-width: 960px)", "33vw"],
              ["(min-width: 768px)", "50vw"],
              ["100vw"],
            ]}
          />
        </div>
      ) : null}
      <div className="lookbook--story--section--lookbook-details">
        <div className="lookbook--story--section--title-bar">
          <div className="lookbook--story--section--avatar">{avatar}</div>
          <div className="lookbook--story--section--lookbook-title">
            {lookbookTitle}
          </div>
        </div>
        {position === 0 ? (
          <div className="lookbook--story--section--instructions">
            <h6>Instructions:</h6>
            <p>
              Tap the right of the screen to go forward &amp; the left of the
              screen to go back. Press and hold the screen or tap the pause
              button in the upper-right to pause.
            </p>
          </div>
        ) : null}
      </div>
      {onPrevious ? (
        <OverlayButton position="left" onPress={onPrevious} />
      ) : null}
      {onNext ? <OverlayButton position="right" onPress={onNext} /> : null}
      <div className="lookbook--story--section--details">
        {sectionTitle || section.description.length ? (
          <div className="space-y-3">
            {sectionTitle ? (
              <h1
                className="lookbook--story--section--title"
                data-test-id="section-title">
                {sectionTitle}
              </h1>
            ) : null}
            {section.description.length ? (
              <p
                className="lookbook--story--section--description"
                data-test-id="section-description">
                {section.description}
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="lookbook--story--section--details--actions">
          <div>
            {showBranding ? <PoweredByEndear className="p-2 -ml-2" /> : null}
          </div>
          <button
            type="button"
            className="lookbook--story--section--button"
            onClick={onNext}>
            {position === 1 ? "Start" : "Next"}
          </button>
        </div>
      </div>
    </section>
  );
}
